import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Block,
  Flex,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
} from '../../../design-system';
import { matchSorter } from 'match-sorter';
import { useToggle } from 'react-use';
import axios from 'axios';
import { Button } from '../../../design-system';
import { Table } from '../../../components/Table';
import { useData } from '../../../helpers/data-loader';
import { checkPermissions, errorMsg } from '../../../helpers/utils';
import { Session } from '../../../contexts/sessionContext';
import { PERMISSIONS } from '../../../globalConstants';
import Alert from '../../../helpers/alert';
import SearchFilter from '../../../components/SearchFilter';

const UserList = () => {
  const {
    state: {
      user: { permissions },
    },
  } = useContext(Session);
  const canEdit = useMemo(
    () => checkPermissions(permissions, [PERMISSIONS.SUPER_ADMIN]),
    [permissions],
  );
  const [users, fetchUsers, loading] = useData('/admin/user', {
    errorMsg: errorMsg('User'),
  });
  const [userToConfirm, setUserToConfirm] = useState<any>();
  const [resetLoading, toggleResetLoading] = useToggle(false);
  const [resetUsers, setResetUsers] = useState<any[]>([]);
  const [filter, setFilter] = useState('');

  const clearUser = () => {
    setUserToConfirm(null);
  };

  const resetPassword = async () => {
    toggleResetLoading();
    try {
      const response = await axios.post('/auth/password/email', {
        email: userToConfirm.email,
      });
      if (response.status === 200) {
        setUserToConfirm(null);
        setResetUsers((prevState) => [...prevState, userToConfirm.id]);
        Alert.success('Email reset link has been sent');
      }
    } catch (error) {
      Alert.error(error.message);
    }
    toggleResetLoading();
  };

  const USER_COLUMNS = [
    {
      Header: 'Name',
      accessor: 'name',
    },
    {
      Header: 'Agency',
      accessor: 'agency.name',
    },
    {
      Header: 'email',
      accessor: 'email',
    },
    {
      Header: 'Reset Password',
      Cell: ({ row: { original } }) => {
        const hasBeenReset = resetUsers.includes(original.id);
        return (
          <Button
            onClick={() => setUserToConfirm(original)}
            variant={hasBeenReset ? 'grey' : 'primary'}
            block
          >
            {hasBeenReset ? 'Re-Send Email' : 'Reset Password'}
          </Button>
        );
      },
    },
  ];

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = useMemo(
    () =>
      matchSorter(users, filter, {
        keys: [
          {
            threshold: matchSorter.rankings.CONTAINS,
            key: 'name',
          },
          {
            threshold: matchSorter.rankings.CONTAINS,
            key: 'agency.name',
          },
          {
            threshold: matchSorter.rankings.CONTAINS,
            key: 'email',
          },
        ],
      }),
    [users, filter],
  );

  return (
    <>
      <Block mt={30}>
        <Flex
          justifyContent="flex-start"
          alignItems="center"
          p="0 36px 18px 26px"
        >
          <Block width={250}>
            <SearchFilter onFilter={setFilter} />
          </Block>
        </Flex>
        <Table
          name="Users"
          columns={USER_COLUMNS}
          data={(filteredUsers as any[]).map((item: any) => ({
            ...item,
            canEdit,
          }))}
          loading={loading}
        />
      </Block>
      <Modal show={userToConfirm} onClose={clearUser}>
        <ModalHeader>
          <Text fontSize="1.2rem" fontWeight={600}>
            Reset Password for {userToConfirm?.name}?
          </Text>
        </ModalHeader>
        <ModalContent>
          <Text display="block" fontSize="1.2rem">
            <Block>
              You are going to reset the password for
              <Text fontWeight={600} ml={1}>
                {userToConfirm?.name}
              </Text>
              .
            </Block>
            <Block mt={3}>An email will be sent to:</Block>
            <Text fontWeight={600} mt={1}>
              {userToConfirm?.email}
            </Text>
            <Block mt={3}>
              This will contain information instructing the user on how to reset
              their password.
            </Block>
          </Text>
        </ModalContent>
        <ModalFooter>
          <Flex justifyContent="flex-end">
            <Button onClick={clearUser} variant="grey">
              Cancel
            </Button>
            <Block ml={3}>
              <Button onClick={resetPassword} loading={resetLoading}>
                Confirm
              </Button>
            </Block>
          </Flex>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default UserList;
